import { ReactNode, createContext, useMemo, useRef, useState } from 'react';

import { useGetInstancesQuery } from '@api/connectInstance';
import { type RdConnectInstance } from '@apiContract';
import axios from 'axios';
import noop from 'lodash/fp/noop';
import { useUserPermissions } from 'src/hooks/useUserPermissions';
import { Roles } from 'src/types/shared';

export enum InstanceStatus {
  ONLINE = 'Online',
  OFFLINE = 'Offline',
}

export type StatusInstance = {
  instance: RdConnectInstance;
  status: InstanceStatus | undefined;
  id: number;
};

type InstanceContext = {
  instances: StatusInstance[] | null;
  isLoading: boolean;
  enabled: boolean;
  refresh: () => void;
};

export const RDConnectContext = createContext<InstanceContext>({
  instances: [],
  isLoading: false,
  enabled: false,
  refresh: noop,
});

type SettingsProviderProps = {
  children: ReactNode;
};

type PingState = { [key: string | number]: InstanceStatus };

const requestPing = (id: string | number) =>
  new Promise<{ id: string | number; status: InstanceStatus }>((res) =>
    axios
      .get(`RdConnectOperations/ping/${id}`)
      .then(() => res({ id, status: InstanceStatus.ONLINE }))
      .catch(() => res({ id, status: InstanceStatus.OFFLINE })),
  );

const RDConnectProviderComponent = ({ children }: SettingsProviderProps) => {
  const isLoaded = useRef<boolean>(false);
  const [pingLoading, setPingLoading] = useState(false);
  const { data: instances = null, isLoading, refetch } = useGetInstancesQuery();

  const [pingState, setPingState] = useState<PingState>({});

  const pingInstances = async () => {
    if (!instances) {
      setPingLoading(false);
      return;
    }

    const ids = instances.map(({ id }) => id);

    if (!ids.length) {
      setPingLoading(false);
      return;
    }
    setPingLoading(true);
    const requests = ids.map((id) => requestPing(+(id || 0)));
    const result = await Promise.allSettled(requests);
    const nextUpdate = (
      result as { value: { id: string | number; status: InstanceStatus } }[]
    ).reduce<PingState | null>((acc, { value: item }) => {
      if (item.id) {
        const nextStatus = item.status;
        if (!acc) {
          return { [item.id]: nextStatus };
        }
        acc[item.id] = nextStatus;
      }

      return acc;
    }, null);

    if (nextUpdate) {
      setPingState(nextUpdate);
    }
    setPingLoading(false);
  };

  const refresh = async () => {
    await refetch();
    await pingInstances();
  };

  if (!isLoaded.current && instances) {
    isLoaded.current = true;
    pingInstances();
  }

  const instancesWithStatus = useMemo<StatusInstance[] | null>(() => {
    if (!instances) return null;

    return instances.map<StatusInstance>((instance) => ({
      instance,
      status: pingState[instance.id as number],
      id: instance.id as number,
    }));
  }, [instances, pingState]);

  return (
    <RDConnectContext.Provider
      value={{ enabled: true, instances: instancesWithStatus, isLoading: isLoading || pingLoading, refresh }}
    >
      {children}
    </RDConnectContext.Provider>
  );
};

export const RDConnectProvider = (props: SettingsProviderProps) => {
  const { children } = props;

  const { isAllowed } = useUserPermissions();
  const isAllow = isAllowed([Roles.Admin, Roles.Mailroom]);

  return isAllow ? <RDConnectProviderComponent>{children}</RDConnectProviderComponent> : children;
};
